import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebaseconf';
import { useAuth } from '../context/AuthContext';
import { completeAuthenticationFlow } from '../utils/authUtils';

export default function SaaSLogin() {
  const navigate = useNavigate();
  const { setUserInfo } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const storedUser = sessionStorage.getItem('logged_user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user.role === 'super_admin') {
          navigate('/super-admin-dashboard');
        } else if (['admin', 'principal'].includes(user.role)) {
          navigate('/admin-dashboard');
        } else if (user.role === 'teacher') {
          navigate('/teacher-dashboard');
        } else if (user.role === 'student') {
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Failed to parse stored user:', err);
        sessionStorage.removeItem('logged_user');
      }
    }
  }, [navigate]);

  /**
   * Handle login with Firebase Auth + Firestore role verification
   * NEW FLOW (Step 4):
   * 1. Validate email/password input
   * 2. Sign in with Firebase Auth
   * 3. Call completeAuthenticationFlow to fetch role from users/{uid}
   * 4. Store user data and redirect based on role
   * 5. Error if users/{uid} role document doesn't exist
   */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Step 1: Validate inputs
      if (!email.trim() || !password.trim()) {
        setError('Please enter email and password');
        setLoading(false);
        return;
      }

      if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        setError('Please enter a valid email address');
        setLoading(false);
        return;
      }

      const normalizedEmail = email.toLowerCase().trim();
      console.log('🔐 Attempting login for:', normalizedEmail);

      // Step 2: Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password);
      const firebaseUser = userCredential.user;
      console.log('✅ Firebase Auth successful:', firebaseUser.uid);

      // Step 3: Call authUtils to complete authentication flow
      // This fetches the role from users/{uid}, validates user exists, and returns redirect URL
      const authResult = await completeAuthenticationFlow(firebaseUser.uid);

      // Step 4: Store user data in context and sessionStorage
      const userToStore = {
        uid: firebaseUser.uid,
        email: authResult.userData.email,
        name: authResult.userData.name,
        role: authResult.userData.role,
        institution_id: authResult.userData.institutionId,
        institutionId: authResult.userData.institutionId,
        profileId: authResult.userData.profileId,
        status: authResult.userData.status
      };

      setUserInfo(userToStore);

      if (rememberMe) {
        localStorage.setItem('remembered_email', normalizedEmail);
      }

      console.log('✅ User authenticated successfully:', userToStore);

      // Step 5: Redirect based on role (from authResult)
      setTimeout(() => {
        navigate(authResult.redirectUrl);
      }, 300);

    } catch (err) {
      console.error('❌ Login error:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);

      // Handle specific Firebase Auth errors
      if (err.code === 'auth/user-not-found') {
        setError('No user found with this email. Please check your email or register first.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else if (err.code === 'auth/user-disabled') {
        setError('This account has been disabled. Contact administrator.');
      } else if (err.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please check your credentials.');
      }
      // Handle custom errors from completeAuthenticationFlow
      else if (err.message && err.message.includes('role document not found')) {
        setError('User authenticated but role information is missing. Contact administrator.');
      } else if (err.message && err.message.includes('Cannot access')) {
        setError('User role verification failed. Please contact support.');
      } else if (err.message?.includes('Cannot read properties')) {
        setError('Database connection error. Please try again.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Load remembered email
  useEffect(() => {
    const remembered = localStorage.getItem('remembered_email');
    if (remembered) {
      setEmail(remembered);
      setRememberMe(true);
    }
  }, []);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          py: 4
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 450 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                Login
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Sign in to your institution account
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                placeholder="admin@institution.com"
                disabled={loading}
                required
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                placeholder="••••••••"
                disabled={loading}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Remember email"
                sx={{ my: 1 }}
              />

              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                sx={{ py: 1.5, mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'LOG IN'}
              </Button>
            </form>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Don't have an account?{' '}
                <Link
                  href="/register-institution"
                  sx={{ cursor: 'pointer', textDecoration: 'none' }}
                >
                  Register Institution
                </Link>
              </Typography>
            </Box>


          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}